import AppModal from "@/components/AppModal";
import { Heading } from "@/components/typography/Heading";
import { Button } from "@/components/ui/button";
import { index as pricingIndex } from "@/routes/pricing";
import type { SharedPageProps } from "@/types/models";
import { Link, usePage } from "@inertiajs/react";
import { useEffect, useState, type PropsWithChildren } from "react";

const sessionKey = "awraq:promotion:seen";
let seenWithoutStorage = false;

const hasSeenPromotion = () => {
  try {
    return Boolean(window.sessionStorage.getItem(sessionKey));
  } catch {
    return seenWithoutStorage;
  }
};

const markPromotionSeen = () => {
  try {
    window.sessionStorage.setItem(sessionKey, "seen");
  } catch {
    // persistence-recovery: The in-memory seen mark prevents another prompt when storage is unavailable.
    seenWithoutStorage = true;
  }
};

export function PromotionLayout({ children }: PropsWithChildren) {
  const page = usePage<SharedPageProps>();
  const promotions = page.props.promotions ?? [];
  const enabled = promotions.some((item) => item.enabled);
  // Demo visitors get the guided demo tour instead; registration still shows the offer after they leave the demo.
  const isDemo = Boolean(page.props.user?.is_demo);
  return (
    <div
      className={`pt-(--promotion-height) ${enabled ? "[--promotion-height:4.875rem] lg:[--promotion-height:4.5rem]" : "[--promotion-height:0px]"}`}
    >
      <PromotionModal
        showBanner
        autoOpen={!isDemo && (page.url.startsWith("/user/") || page.url.startsWith("/auth/register/info"))}
      />
      {children}
    </div>
  );
}

function PromotionModal({ showBanner = false, autoOpen = true }: { showBanner?: boolean; autoOpen?: boolean }) {
  const promotions = (usePage<SharedPageProps>().props.promotions ?? []).filter((item) => item.enabled);
  const enabled = promotions.length > 0;
  // Read once per mount: the offer opens at most once per browser session.
  const [unseenThisSession] = useState(() => !hasSeenPromotion());
  const [dismissed, setDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const nationalDay = promotions.some((promotion) => promotion.id === "national-day-96");
  const bannerTitle = promotions.length > 1 ? "العروض الحالية" : promotions[0]?.title;
  const activePromotion = promotions[activeIndex] ?? promotions[0];
  const hasNextPromotion = activeIndex + 1 < promotions.length;

  const open = enabled && autoOpen && unseenThisSession && !dismissed;

  useEffect(() => {
    if (open) {
      markPromotionSeen();
    }
  }, [open]);

  if (!enabled) {
    return null;
  }

  return (
    <>
      {showBanner && (
        <div
          dir="rtl"
          aria-label="العروض الحالية"
          className="text-primary-foreground fixed inset-x-0 top-0 z-40 flex h-(--promotion-height) items-center justify-center gap-3 border-b border-[oklch(0.46_0.085_152)] bg-[oklch(0.352_0.076_152)] px-3 lg:px-36"
        >
          <div className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center lg:flex-none">
            {nationalDay && (
              <span className="text-[10px] font-medium text-white/65 lg:text-[11px]">
                عروض اليوم الوطني 96 · حتى 30 سبتمبر 2026
              </span>
            )}
            <span className="text-sm leading-tight font-bold lg:text-lg">
              {nationalDay ? (
                <>
                  خصم حتى <span className="text-primary">70٪</span> على باقات الأفراد
                </>
              ) : (
                bannerTitle
              )}
            </span>
          </div>
          <Button
            asChild
            className="h-8 shrink-0 px-3 text-xs lg:absolute lg:start-3.5 lg:top-1/2 lg:h-9 lg:-translate-y-1/2 lg:px-4 lg:text-sm"
          >
            <Link href={pricingIndex()} data-testid="promotion-banner">
              عرض العروض
            </Link>
          </Button>
        </div>
      )}
      <AppModal
        open={open}
        onOpenChange={(nextOpen) => setDismissed(!nextOpen)}
        desktopSize="default"
        desktopHeight="90vh"
        mobileHeight="content"
        padding="none"
      >
        <div className="p-3 pt-12 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:p-4">
          {[activePromotion].map((promotion) => (
            <article
              key={promotion.id ?? promotion.image_url}
              className="min-w-0 rounded-xl bg-stone-50 p-2"
              dir="rtl"
              data-testid="promotion-modal"
            >
              <div className="mx-auto flex max-w-[27rem] flex-col gap-3">
                <Heading level={2} size="section" className="sr-only">
                  {promotion.title}
                </Heading>
                <img
                  src={promotion.image_url}
                  alt={promotion.title}
                  className="aspect-[3/4] max-h-[max(6rem,calc(75svh-14rem))] w-full rounded-xl object-contain md:max-h-[60svh]"
                />
                <Button
                  asChild
                  size="lg"
                  className="h-auto min-h-11 w-full bg-green-700 px-2 text-xs whitespace-normal text-white hover:bg-green-800 sm:text-sm"
                >
                  <a href={promotion.cta.url} target="_blank" rel="noreferrer">
                    {promotion.cta.label}
                  </a>
                </Button>
              </div>
            </article>
          ))}
          {promotions.length > 1 && (
            <Button
              variant="ghost"
              className="mt-2 w-full"
              onClick={() => (hasNextPromotion ? setActiveIndex(activeIndex + 1) : setDismissed(true))}
            >
              {hasNextPromotion ? "العرض التالي" : "إغلاق"} · {activeIndex + 1}/{promotions.length}
            </Button>
          )}
        </div>
      </AppModal>
    </>
  );
}
